import { Delete } from "@dreamhub/designsystem/icons";
import DateTimeField from "@/components/controls/DateTimeField";
import { SingleSelectOptions } from "@/components/controls/Form/Form";
import SingleSelect from "@/components/controls/SingleSelect";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import { reminderTimePeriods } from "@/entities/tasks/helpers";
import {
  ReminderTimePeriod,
  Reminder as ReminderType
} from "@/entities/tasks/types";
import { getEnumTKey } from "@/utils/strings";
import { addWeeks } from "date-fns";
import { map } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  reminder: ReminderType;
  updateRemindersState: (
    reminderId: string,
    fieldName: keyof ReminderType,
    newValue: number | ReminderTimePeriod | string | null
  ) => void;
  handleDeleteReminder: (reminderId: string) => void;
  showDeleteIcon?: boolean;
}

const Reminder: React.FC<Props> = ({
  reminder,
  updateRemindersState,
  handleDeleteReminder,
  showDeleteIcon = true
}) => {
  const { t } = useTranslation();
  const { id, value, timePeriod, customDate } = reminder;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [previousTimePeriod, setPreviousTimePeriod] = useState(timePeriod);

  const timePeriodOptions = useMemo(
    () =>
      map(reminderTimePeriods, (timePeriod) => ({
        id: timePeriod,
        name: ReminderTimePeriod[timePeriod]
      })),
    [reminderTimePeriods]
  );

  useEffect(() => {
    if (timePeriod !== previousTimePeriod) {
      setPreviousTimePeriod(timePeriod);

      if (timePeriod === ReminderTimePeriod.CUSTOM && !customDate) {
        const today = new Date();
        const nextWeekDate = addWeeks(today, 1);
        updateRemindersState(id, "customDate", nextWeekDate.toISOString());
      } else if (timePeriod !== ReminderTimePeriod.CUSTOM) {
        updateRemindersState(id, "customDate", null);
      }
    }
  }, [timePeriod, previousTimePeriod, customDate, id, updateRemindersState]);

  return (
    <div className="flex gap-2">
      {customDate ? (
        <DateTimeField
          type="date"
          value={customDate}
          onChange={(value) => {
            updateRemindersState(id, "customDate", value);
          }}
          isOpen={isDatePickerOpen}
          onOpenChange={(open) => setIsDatePickerOpen(open)}
          name="customDate"
          iconPosition="left"
          variant="badge"
          isDueDate
          showDatePresets
          className="h-full"
        />
      ) : (
        <Input
          type="number"
          className="flex-1 w-20 h-full pl-2 py-[6px] pr-2 border-border-border2"
          onChange={(e) => {
            updateRemindersState(id, "value", Number(e.target.value));
          }}
          value={value ?? 1}
          min={1}
        />
      )}
      <div className="flex-1 flex gap-2 w-56 items-center">
        <SingleSelect
          fieldName="timePeriod"
          options={timePeriodOptions as SingleSelectOptions[]}
          selectedValue={timePeriod as number}
          onSelection={(value) => {
            updateRemindersState(id, "timePeriod", value as ReminderTimePeriod);
          }}
          formatter={(value) =>
            t(
              getEnumTKey(
                "tasks",
                "timePeriods",
                ReminderTimePeriod,
                value as number
              )
            )
          }
          variant="form"
          className="p-2"
          mainContainerClassName="w-full"
        />
        {showDeleteIcon && (
          <IconButton
            variant="iconNoBg"
            onClick={() => handleDeleteReminder(id)}
            iconColor="secondary"
            size="md"
          >
            <Delete />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default Reminder;
